"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    SparklesIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    ArrowRightIcon,
    LightBulbIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon,
    TagIcon,
    ClockIcon,
    HeartIcon,
    AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import { SparklesIcon as SparklesSolid } from "@heroicons/react/24/solid";
import { FitnessClass } from "@/types/fitness-class";

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

interface ParsedIntent {
    categories?: string[];
    difficulty?: string[];
    timeOfDay?: string | null;
    dayPreference?: string | null;
    mood?: string | null;
    healthConsiderations?: string[];
    durationMin?: number | null;
    durationMax?: number | null;
}

interface SmartSearchResponse {
    success: boolean;
    data: {
        classes: FitnessClass[];
        totalResults: number;
        aiExplanation: string;
        parsedIntent: ParsedIntent;
        searchQuery: string;
    };
    error?: string;
}

interface AISmartSearchProps {
    onResultsChange: (classes: FitnessClass[] | null, isAIMode: boolean) => void;
    isAIMode: boolean;
    onToggleAIMode: (enabled: boolean) => void;
    initialQuery?: string | null;
    shouldAutoSearch?: boolean; // New prop to control auto-search
}

// ────────────────────────────────────────────────────────────────
// Suggestion Chips
// ────────────────────────────────────────────────────────────────

const SMART_SUGGESTIONS = [
    "Gentle yoga for back pain",
    "30 min morning meditation",
    "High energy dance class",
    "Beginner-friendly yoga",
    "Relaxing evening class",
    "Prenatal wellness",
    "Classes for seniors",
    "Short breathing exercises",
    "Stress relief session",
    "Fun kids activities",
];

// ────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────

export default function AISmartSearch({
    onResultsChange,
    isAIMode,
    onToggleAIMode,
    initialQuery,
    shouldAutoSearch = true, // Default to true for backward compatibility
}: AISmartSearchProps) {
    const [query, setQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [aiExplanation, setAiExplanation] = useState("");
    const [parsedIntent, setParsedIntent] = useState<ParsedIntent | null>(null);
    const [totalResults, setTotalResults] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const searchContainerRef = useRef<HTMLDivElement>(null);

    // Track if auto-search has been performed for this session
    // Using a ref that persists across re-renders but resets on unmount
    const hasAutoSearchedRef = useRef(false);
    const initialQueryProcessedRef = useRef<string | null>(null);

    // Focus input when AI mode is toggled on
    useEffect(() => {
        if (isAIMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isAIMode]);

    // Auto-search when initialQuery is provided (e.g., from homepage redirect)
    // This runs ONLY ONCE on component mount
    useEffect(() => {
        const performAutoSearch = async () => {
            // Don't auto-search if explicitly disabled
            if (!shouldAutoSearch) {
                console.log("⏭️ Auto-search disabled by parent component");
                return;
            }

            // Check if we've already processed this exact query
            if (initialQueryProcessedRef.current === initialQuery) {
                console.log("⏭️ Skipping auto-search - query already processed:", initialQuery);
                return;
            }

            // Only auto-search if we have an initial query and it's long enough
            if (!initialQuery || initialQuery.trim().length < 3) {
                return;
            }

            // Check if we've already done an auto-search in this session
            if (hasAutoSearchedRef.current) {
                console.log("⏭️ Skipping auto-search - already performed in this session");
                return;
            }

            console.log("🏠 Auto-search triggered from homepage:", initialQuery);

            // Mark this query as processed and set the flag
            initialQueryProcessedRef.current = initialQuery;
            hasAutoSearchedRef.current = true;

            setQuery(initialQuery);
            setIsSearching(true);
            setError(null);
            setAiExplanation("");
            setParsedIntent(null);
            setHasSearched(true);

            // Small delay so component is fully mounted
            await new Promise(resolve => setTimeout(resolve, 500));

            try {
                console.log("🔍 Executing auto-search for:", initialQuery);
                const response = await fetch("/api/ai/smart-search", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ query: initialQuery }),
                });

                const data: SmartSearchResponse = await response.json();

                if (!response.ok || data.error) {
                    setError(data.error || "Search failed. Please try again.");
                    onResultsChange(null, true);
                    return;
                }

                if (data.success && data.data) {
                    console.log("✅ Auto-search results:", data.data.totalResults, "classes");
                    setAiExplanation(data.data.aiExplanation);
                    setParsedIntent(data.data.parsedIntent);
                    setTotalResults(data.data.totalResults);
                    onResultsChange(data.data.classes, true);
                }
            } catch (err) {
                console.error("❌ Auto-search error:", err);
                setError("Something went wrong. Please try again.");
                onResultsChange(null, true);
            } finally {
                setIsSearching(false);
            }
        };

        // Only run on mount
        performAutoSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // EMPTY DEPS - only run on mount, never again

    // Main search function
    const handleSearch = useCallback(
        async (searchQuery?: string) => {
            const q = (searchQuery || query).trim();
            if (!q || q.length < 3) {
                setError("Please type at least 3 characters to search");
                return;
            }

            setIsSearching(true);
            setError(null);
            setAiExplanation("");
            setParsedIntent(null);
            setHasSearched(true);

            try {
                const response = await fetch("/api/ai/smart-search", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ query: q }),
                });

                const data: SmartSearchResponse = await response.json();

                if (!response.ok || data.error) {
                    setError(data.error || "Search failed. Please try again.");
                    onResultsChange(null, true);
                    return;
                }

                if (data.success && data.data) {
                    setAiExplanation(data.data.aiExplanation);
                    setParsedIntent(data.data.parsedIntent);
                    setTotalResults(data.data.totalResults);
                    onResultsChange(data.data.classes, true);
                }
            } catch (err) {
                console.error("Smart search error:", err);
                setError("Something went wrong. Please try again.");
                onResultsChange(null, true);
            } finally {
                setIsSearching(false);
            }
        },
        [query, onResultsChange]
    );

    // Handle enter key
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !isSearching) {
            handleSearch();
        }
    };

    // Handle suggestion click
    const handleSuggestionClick = (suggestion: string) => {
        setQuery(suggestion);
        handleSearch(suggestion);
    };

    // Clear AI search
    const handleClear = () => {
        setQuery("");
        setAiExplanation("");
        setParsedIntent(null);
        setTotalResults(0);
        setError(null);
        setHasSearched(false);
        onResultsChange(null, true);
        if (inputRef.current) inputRef.current.focus();
    };

    // Toggle off AI mode
    const handleToggleOff = () => {
        console.log("🔄 Switching from AI to Regular search");

        // Clear all AI state
        setQuery("");
        setAiExplanation("");
        setParsedIntent(null);
        setTotalResults(0);
        setError(null);
        setHasSearched(false);

        // Notify parent to clear AI results and disable AI mode
        onResultsChange(null, false);
        onToggleAIMode(false);

        console.log("✅ Switched to Regular search - showing all classes");
    };

    if (!isAIMode) {
        console.log("🚫 AISmartSearch hidden - isAIMode is false");
        // Component stays mounted but parent controls visibility
        // Return empty fragment to avoid rendering
        return <></>;
    }

    return (
        <div ref={searchContainerRef} className="w-full">
            {/* AI Search Input with Switch Button */}
            <div className="relative w-full flex flex-row items-center flex-nowrap gap-2 sm:gap-3">
                {/* AI Search Input Container */}
                <div className="flex-1">
                    <div className="relative group">
                        {/* Animated border gradient */}
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 rounded-2xl opacity-75 group-hover:opacity-100 transition-opacity duration-500 blur-sm animate-pulse" />

                        <div className="relative bg-white rounded-2xl shadow-xl">
                            <div className="flex items-center">
                                {/* AI Icon */}
                                <div className="pl-3 sm:pl-5 pr-1 sm:pr-2 flex-shrink-0">
                                    {isSearching ? (
                                        <ArrowPathIcon className="w-4 h-4 sm:w-6 sm:h-6 text-purple-500 animate-spin" />
                                    ) : (
                                        <SparklesSolid className="w-4 h-4 sm:w-6 sm:h-6 text-purple-500" />
                                    )}
                                </div>

                                <input
                                    ref={inputRef}
                                    id="ai-smart-search-input"
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder='Try: "Gentle yoga for back pain..."'
                                    disabled={isSearching}
                                    className="flex-1 py-3 sm:py-4 px-1 sm:px-2 text-sm sm:text-base text-gray-800 placeholder:text-gray-400 bg-transparent border-none outline-none focus:ring-0 disabled:opacity-50"
                                    autoComplete="off"
                                />

                                {/* Action buttons */}
                                <div className="flex items-center gap-1 pr-2 sm:pr-3 flex-shrink-0">
                                    {query && (
                                        <button
                                            onClick={handleClear}
                                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                                            title="Clear search"
                                        >
                                            <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </button>
                                    )}

                                    {/* Search button */}
                                    <button
                                        onClick={() => handleSearch()}
                                        disabled={isSearching || !query.trim()}
                                        className="p-2 sm:p-2.5 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-xl transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg hover:shadow-purple-300/50 active:scale-95"
                                        title="AI Search"
                                    >
                                        <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AI Mode Label */}
                    {/* <div className="flex items-center gap-1.5 mt-2 px-1">
                        <SparklesSolid className="w-3 h-3 text-purple-500" />
                        <span className="text-xs text-purple-600 font-bold uppercase tracking-wider">
                            Prana AI Search
                        </span>
                    </div> */}
                </div>

                <button
                    onClick={handleToggleOff}
                    className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-3 sm:py-4 rounded-xl sm:rounded-2xl border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 hover:from-orange-100 hover:to-amber-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-200/40 transition-all duration-300 shadow-lg group"
                    title="Switch to regular search with filters"
                >
                    <AdjustmentsHorizontalIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:rotate-90 transition-transform duration-300" />
                    <span className="hidden sm:inline text-sm font-medium">Regular Search</span>
                    <span className="sm:hidden text-xs sm:text-sm font-medium">Switch</span>
                </button>
            </div>

            {/* Suggestions (show when no search has been performed) */}
            {!hasSearched && !isSearching && (
                <div className="mt-4 animate-fadeIn">
                    <div className="flex items-center gap-2 mb-3">
                        <LightBulbIcon className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-gray-500 font-medium">
                            Try asking:
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {SMART_SUGGESTIONS.slice(0, 6).map((suggestion, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100 text-sm text-purple-700 hover:bg-purple-100 hover:border-purple-200 transition-all duration-200 cursor-pointer active:scale-95"
                            >
                                <SparklesIcon className="w-3 h-3" />
                                {suggestion}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Loading State */}
            {isSearching && (
                <div className="mt-6 flex flex-col items-center py-8 animate-fadeIn">
                    <div className="relative">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 animate-pulse flex items-center justify-center">
                            <SparklesSolid className="w-8 h-8 text-white animate-bounce" />
                        </div>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 opacity-30 animate-ping" />
                    </div>
                    <p className="mt-4 text-sm text-purple-600 font-medium animate-pulse">
                        AI is analyzing your request...
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                        Understanding preferences, finding matching classes, ranking results
                    </p>
                </div>
            )}

            {/* Error State */}
            {error && !isSearching && (
                <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 animate-fadeIn">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm text-red-700">{error}</p>
                        <button
                            onClick={() => handleSearch()}
                            className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                        >
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {/* AI Explanation Block */}
            {aiExplanation && !isSearching && (
                <div className="mt-4 animate-fadeIn">
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border border-purple-100 p-4 sm:p-5">
                        {/* Decorative sparkles */}
                        <div className="absolute top-2 right-3 opacity-20">
                            <SparklesSolid className="w-12 h-12 text-purple-400" />
                        </div>

                        {/* AI Header */}
                        <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 shadow-md">
                                <SparklesSolid className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm font-semibold text-purple-800">
                                AI Recommendation
                            </span>
                            <span className="text-xs text-purple-400 ml-auto">
                                {totalResults} {totalResults === 1 ? "class" : "classes"} found
                            </span>
                        </div>

                        {/* Explanation text */}
                        <p className="text-sm text-gray-700 leading-relaxed relative z-10">
                            {aiExplanation}
                        </p>

                        {/* Parsed intent tags */}
                        {parsedIntent && (
                            <div className="mt-3 pt-3 border-t border-purple-100/50">
                                <div className="flex flex-wrap gap-1.5">
                                    {parsedIntent.categories &&
                                        parsedIntent.categories.length > 0 && (
                                            <>
                                                {parsedIntent.categories.slice(0, 3).map((cat, i) => (
                                                    <span
                                                        key={`cat-${i}`}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100/70 text-xs text-purple-700"
                                                    >
                                                        <TagIcon className="w-3 h-3" />
                                                        {cat}
                                                    </span>
                                                ))}
                                            </>
                                        )}

                                    {parsedIntent.difficulty &&
                                        parsedIntent.difficulty.length > 0 && (
                                            <>
                                                {parsedIntent.difficulty.map((d, i) => (
                                                    <span
                                                        key={`diff-${i}`}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100/70 text-xs text-blue-700 capitalize"
                                                    >
                                                        {d}
                                                    </span>
                                                ))}
                                            </>
                                        )}

                                    {parsedIntent.timeOfDay && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100/70 text-xs text-amber-700 capitalize">
                                            <ClockIcon className="w-3 h-3" />
                                            {parsedIntent.timeOfDay}
                                        </span>
                                    )}

                                    {parsedIntent.mood && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100/70 text-xs text-green-700 capitalize">
                                            {parsedIntent.mood}
                                        </span>
                                    )}

                                    {parsedIntent.healthConsiderations &&
                                        parsedIntent.healthConsiderations.length > 0 && (
                                            <>
                                                {parsedIntent.healthConsiderations.map((h, i) => (
                                                    <span
                                                        key={`health-${i}`}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100/70 text-xs text-rose-700"
                                                    >
                                                        <HeartIcon className="w-3 h-3" />
                                                        {h}
                                                    </span>
                                                ))}
                                            </>
                                        )}

                                    {(parsedIntent.durationMin || parsedIntent.durationMax) && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-100/70 text-xs text-teal-700">
                                            <ClockIcon className="w-3 h-3" />
                                            {parsedIntent.durationMin === parsedIntent.durationMax
                                                ? `${parsedIntent.durationMin} min`
                                                : parsedIntent.durationMin && parsedIntent.durationMax
                                                    ? `${parsedIntent.durationMin}-${parsedIntent.durationMax} min`
                                                    : parsedIntent.durationMax
                                                        ? `Up to ${parsedIntent.durationMax} min`
                                                        : `${parsedIntent.durationMin}+ min`}
                                        </span>
                                    )}

                                    {parsedIntent.dayPreference && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100/70 text-xs text-indigo-700 capitalize">
                                            {parsedIntent.dayPreference}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
